import React, { useState, useEffect } from "react";
import * as S from "../css/Edit.styles";
import MenuComponent from "../component/MenuComponent";
import { useRecoilValue } from "recoil";
import useGetEditData from "../api/edit/useGetEditData";
import { editDataState } from "../stores/edit/editDataState";
import { useEditTmDv } from "../api/edit/useEditTmDv";
import Swal from "sweetalert2";
import { loginState } from "../stores/loginState";
import Loading from "../component/Loading";
import { formatNumberCommas } from "../utils/formatNumberCommas";

function Edit(props) {
  // NOTE 상태 변수 선언
  const login = useRecoilValue(loginState); // 로그인 전역변수
  const [searchDate, setSearchDate] = useState(new Date().toISOString().slice(0, 10)); // 조회일자
  const [workType, setWorkType] = useState("전체"); // 업무구분
  const [carNum, setCarNum] = useState(""); // 차량번호
  const [groupedData, setGroupedData] = useState([]); // 업무수정 할 차량번호별로 그룹화한 데이터집합
  const [selectedTmDv, setSelectedTmDv] = useState({}); // 선택된 업무구분
  const editData = useRecoilValue(editDataState); //
  const [isLoading, setIsLoading] = useState(false); // 로딩 상태

  // TODO 수정할 데이터 호출 API
  const { mutate: getEditData } = useGetEditData({
    date: searchDate,
    workType,
    carNum,
  });

  // TODO 데이터 저장 API
  const { mutate: updateTmDv } = useEditTmDv();
  
  // NOTE 검색 버튼 클릭 시
  const handleSearch = async () => {
    setIsLoading(true);
    try {
      await getEditData();
      Swal.fire({
        icon: "success",
        title: "업무수정 데이터를\n성공적으로 불러왔습니다!",
        confirmButtonText: "확인",
      });
    } catch (error) {
      console.error("Error fetching edit data:", error);
    } finally {
      setIsLoading(false); // 데이터 처리 완료
    }
  };

  // NOTE 조회된 데이터 그룹화
  useEffect(() => {
    // 굳이 안에 const 함수 만든 이유는 async를 이용해 코드 처리 순서를 명확히 하기 위해
    const processEditData = async () => {
      if (editData) {
        const dataWithCount = addCountToData(editData);
        const grouped = groupAndSumData(dataWithCount);
        setGroupedData(grouped);
        setSelectedTmDv(
          grouped.reduce((acc, item) => {
            acc[item.scandate + item.car_num] = item.tm_dv;
            return acc;
          }, {})
        );
      }
    };
  
    processEditData();
  }, [editData]);

  // 특정 조건에 맞는 데이터 축적
  const addCountToData = (data) => {
    return data.map((item) => ({
      ...item,
      cnt: item.someFieldForCountCalculation,
    }));
  };

  // NOTE 업무구분, 차량번호에 따라 조회된 데이터 집합 정렬
  const groupAndSumData = (data) => {
    const groupedData = data.reduce((acc, item) => {
      const key = `${item.scandate}-${item.tm_dv}-${item.car_num}`;
      if (!acc[key]) {
        acc[key] = {
          scandate: item.scandate,
          tm_dv: item.tm_dv,
          car_num: item.car_num,
          cnt: 0,
        };
      }
      acc[key].cnt++;
      return acc;
    }, {});
  
    // 정렬 기준
    return Object.values(groupedData).sort((a, b) => {
      if (a.tm_dv < b.tm_dv) return 1; // 업무구분 순서
      if (a.tm_dv > b.tm_dv) return -1;
      if (a.car_num < b.car_num) return -1; // 차량번호 순서
      if (a.car_num > b.car_num) return 1;
      return 0;
    });
  };
  
  // NOTE 검색된 특정 집합 업무구분 수정
  const handleTmDvChange = (scandate, car_num, newTmDv) => {
    setSelectedTmDv((prev) => ({
      ...prev,
      [scandate + car_num]: newTmDv
    }));
  };
  
  // NOTE 수정한 데이터 집합 저장하는 함수
  const handleSave = async (scandate, car_num) => {
    setIsLoading(true);
    const tm_dv = selectedTmDv[scandate + car_num];
  
    try {
      const searchList = editData
        .filter((item) => item.scandate === scandate && item.car_num === car_num)
        .map((item) => ({
          ...item,
          tm_dv: tm_dv,
        }));
  
      if (searchList.length === 0) {
        throw new Error("검색 목록이 비어 있습니다.");
      }
      if (searchList.some(item => !item.id || !item.barcode)) {
        throw new Error("필수 필드가 누락되었습니다.");
      }
      
      //API 호출, 호출 완료 시 메시지 출력
      await updateTmDv({
        list: searchList,
        tm_dv: tm_dv,
        bran_cd: login.branchCode,
        company: login.company
      });
    } catch (error) {
      Swal.fire({
        title: "오류 발생",
        icon: "error",
        text: error.message || "데이터 처리 중 오류가 발생했습니다. 다시 시도해주세요.",
        confirmButtonText: "확인"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // NOTE 엔터 키 누르면 검색
  const handleKeyDownEnter = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <S.DashboardContainer onKeyDown={handleKeyDownEnter}>
      <MenuComponent theme={props.theme} />
      <S.ContentContainer>
      {isLoading && <Loading />}
        <S.MainContainer>
          <S.ModifyContainer>
            <h4>업무수정</h4>
            <S.SearchContainer>
              <p>조회일자</p>
              <S.Input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
              />
              <div style={{ margin: "0 10px" }}>|</div>
              <p>업무구분</p>
              <S.SelectContainer onChange={(e) => setWorkType(e.target.value)}>
                <option>전체</option>
                <option value={login.company === "LOGEN" ? "60" : "21"}>{login.company === "LOGEN" ? "배송입고 (60)" : "도착 (21)"}</option>
                <option value={login.company === "LOGEN" ? "30" : "20"}>{login.company === "LOGEN" ? "집하출고 (30)" : "발송 (20)"}</option>
              </S.SelectContainer>
              <div style={{ margin: "0 10px" }}>|</div>
              <p>차량번호</p>
              <S.Input
                type="number"
                placeholder="차량번호를 입력해주세요."
                value={carNum}
                onChange={(e) => setCarNum(e.target.value)}
              />
              <S.ButtonContainer onClick={handleSearch}>검색</S.ButtonContainer>
              {/* <S.ButtonContainer onClick={window.location.href="https://jhctaekbaein.jhcon.net/"}>택배인</S.ButtonContainer> */}
            </S.SearchContainer>
            <hr />
            <S.TableContainer>
              <S.Table>
                <thead>
                  <tr>
                    <S.TableHeader>No.</S.TableHeader>
                    <S.TableHeader>조회일자</S.TableHeader>
                    <S.TableHeader>업무구분</S.TableHeader>
                    <S.TableHeader>차량번호</S.TableHeader>
                    <S.TableHeader>운송장 건수</S.TableHeader>
                    <S.TableHeader>변경사항 저장</S.TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {groupedData &&
                    groupedData.map((item, index) => (
                      <tr key={index} style={{backgroundColor: index % 2 === 0 ? props.theme.white2 : "unset"}}>
                        <S.TableData>{index + 1}</S.TableData>
                        <S.TableData>{item.scandate}</S.TableData>
                        <S.TableData>
                          <S.SelectContainer
                            key={item.tm_dv} defaultValue={item.tm_dv}
                            onChange={(e) => handleTmDvChange(item.scandate, item.car_num, e.target.value)}
                          >
                          <option value={login.company === "LOGEN" ? "60" : "21"}>{login.company === "LOGEN" ? "배송입고 (60)" : "도착 (21)"}</option>
                          <option value={login.company === "LOGEN" ? "30" : "20"}>{login.company === "LOGEN" ? "집하출고 (30)" : "발송 (20)"}</option>
                          </S.SelectContainer>
                        </S.TableData>
                        <S.TableData>{item.car_num}</S.TableData>
                        <S.TableData>{formatNumberCommas(item.cnt)} 건</S.TableData>
                        <S.TableData>
                          <S.ButtonContainer onClick={() => handleSave(item.scandate, item.car_num)}>
                            저장하기
                          </S.ButtonContainer>
                        </S.TableData>
                      </tr>
                    ))}
                </tbody>
              </S.Table>
            </S.TableContainer>
          </S.ModifyContainer>
        </S.MainContainer>
      </S.ContentContainer>
    </S.DashboardContainer>
  );
}

export default Edit;
